import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'

import { averageScore } from '../../helpers/OfferHelpers'
import { actions, selectors } from '../../redux'
import { ActionButtonVariant } from '../../components/ActionButton/styled'
import imageFeedBacksTransparent from '../../components/MembersFeedback/image/img-feedback-not-connected.png'
import { useServiceRequest } from '../../hooks/useServiceRequest'
import { ImageTransformer } from '../Transformers/ImageTransformer'
import { Configuration } from '../../rest/types/Configuration'
import { MembersFeedbackProps } from '../../components/MembersFeedback'

const useFeedbackProps = (
  offerId: string,
  setIsAddFeedbackModalVisible: (bool: boolean) => void,
  setLocked: (bool: boolean) => void,
  configuration: Configuration
): MembersFeedbackProps | undefined => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const user = useSelector(selectors.auth.user)
  const isConnected = useSelector(selectors.auth.isConnected)
  const isMember = useSelector(selectors.auth.isSubscriptionActive)
  const hasAccessToStore = useSelector(selectors.auth.hasAccessToStore)
  const addFeedback = useSelector(selectors.feedbacks.addFeedback)
  const updatedFeedback = useSelector(
    selectors.feedbacks.currentUpdatedFeedback
  )

  const [feedbackResponse, handleLoad] = useServiceRequest(
    selectors.feedbacks.load,
    actions.feedbacks.loadRequest,
    actions.feedbacks.loadReset
  )

  console.log('FEEDBACK RESPONSE', feedbackResponse)

  const [deleteResponse, handleDelete] = useServiceRequest(
    selectors.feedbacks.deleteFeedback,
    actions.feedbacks.deleteFeedbackRequest,
    actions.feedbacks.deleteFeedbackReset
  )

  const { data, total, userFeedback } = feedbackResponse.data ?? {}

  const average = averageScore(
    data?.map((item: any) => parseInt(item.rating)) ?? []
  )

  const limit = 4

  const [offset, setOffset] = useState(0)
  const isFirstLoad = offset === 0 && feedbackResponse.pending
  const loadMore = () => {
    setOffset((prevState) => prevState + limit)
  }

  useEffect(() => {
    if (addFeedback.success) {
      setOffset((before) => {
        if (before === 0) {
          handleLoad({ id: offerId, limit, offset: 0 })
        }
        return 0
      })
      dispatch(actions.feedbacks.addFeedbackReset())
    }
  }, [dispatch, addFeedback.success, handleLoad, offerId])

  useEffect(() => {
    if (deleteResponse.success) {
      handleLoad({ id: offerId, limit, offset })
      dispatch(actions.feedbacks.deleteFeedbackReset())
    }
  }, [dispatch, deleteResponse.success, handleLoad, offset, offerId])

  useEffect(() => {
    if (isConnected && offerId) {
      handleLoad({ id: offerId, limit, offset })
    }
  }, [isConnected, offerId, handleLoad, offset, updatedFeedback])

  return {
    title: t('member_feedbacks_title'),
    numberFeedback: `${feedbackResponse?.data?.total} ${t(
      'member_feedbacks_label'
    )}`,
    ...(average != 0 && {
      averageScore: {
        totalStars: 5,
        activeStars: average,
      },
    }),
    ...(data && {
      ...(!data?.length &&
        !isFirstLoad && {
          noFeedbackText: t('no_feedback_text'),
        }),
      memberFeedbackItem: data?.map((item: any) => ({
        avatar: {
          photo: ImageTransformer(
            (item?.user?.avatar as any) ??
              (configuration.config.defaultAvatar as any)
          ),
        },
        memberOpinion: item?.text,
        memberName:
          item?.user?.firstName && item?.user?.lastName
            ? `${item?.user?.firstName} ${item?.user?.lastName}`
            : t('feedback_user_removed'),
        memberDate: t('member_feedbacks_date', {
          date: new Date(item?.date),
        }),
        memberScore: item?.rating,

        ...(!!user?.id &&
          !!item?.user?.id &&
          String(user.id) === String(item.user.id) && {
            actions: {
              editOnClick: () => {
                setIsAddFeedbackModalVisible(true)
                setLocked(true)
                dispatch(actions.feedbacks.setCurrentUpdatedFeedback(item))
              },
              deleteOnClick: () => {
                console.log('ITEM', item)
                handleDelete({
                  feedback: item.documentId ?? item.id,
                })
              },
            },
          }),
      })),

      ...(data.length &&
        data.length < (total ?? 0) && {
          link: {
            label: t('member_feedbacks_see_more_text'),
            onClick: loadMore,
          },
        }),
    }),

    // a user can only leave one feedback per offer: hide the button as soon
    // as they have one (pending or published)
    ...(isConnected &&
      isMember &&
      hasAccessToStore &&
      !userFeedback && {
        button: {
          text: t('member_feedbacks_button_text'),
          variant: ActionButtonVariant.tertiary,
          onClick: () => {
            setIsAddFeedbackModalVisible(true)
            setLocked(true)
          },
        },
      }),

    ...(userFeedback?.status === 'pending' && {
      pendingFeedbackText: t('feedback_pending_text'),
    }),

    isLogInAndMember: isConnected && (isMember || hasAccessToStore),
    imageFeedBacksTransparent: {
      src: imageFeedBacksTransparent,
      alt: t('feedback_transparent_image_alt'),
    },
    isFirstLoading: isFirstLoad,
    isMoreLoading: !isFirstLoad && feedbackResponse.pending,
  }
}

export default useFeedbackProps
